import i18next from 'i18next'
import React from 'react'
import { Link } from '../../utils/link'
import { ParsedPhoneNumber } from '../../utils/phoneNumber'
import { PageHeaderNavLogo, DepartmentIcon, SpaarneCentrumType, SpaarneDepartmentType } from '../../utils/spaarneIcons'
import { ContactItem } from '../cmsPage/cmsPageState'
import { convertRemToPx } from '../../utils/utils'
import { ComponentDidMount, ComponentWillUnmount } from '../../utils/lifecycleMethods'

type PageheaderQuicklinksProps = { appointmentUrl?: string; waitingTimesUrl?: string; display?: string }

export const PageHeaderQuicklinksLayout = (props: PageheaderQuicklinksProps) => (
  <section className={`nav__quicklinks ${props.display && props.display}`}>
    <p className="sr-text">{i18next.t('common:quickActions')}</p>
    <ul>
      {props.appointmentUrl && (
        <li className="nav__item">
          <svg className="nav__icon" width="30px" height="30px">
            <use xlinkHref="#icon--SG-afspraak-maken" />
          </svg>
          <Link url={props.appointmentUrl} className="nav__link">
            <span>{i18next.t('misc:makeAnAppointment')}</span>
          </Link>
        </li>
      )}
      {props.waitingTimesUrl && (
        <li className="nav__item">
          <svg className="nav__icon" width="30px" height="30px">
            <use xlinkHref="#icon--SG-wachttijden" />
          </svg>
          <Link url={props.waitingTimesUrl} className="nav__link">
            <span>{i18next.t('misc:waitingTimes')}</span>
          </Link>
        </li>
      )}
    </ul>
  </section>
)

export const FriendPageHeaderQuicklinksLayout = (props: { displayClassName: string }) => (
  <section className={`nav__quicklinks ${props.displayClassName}`}>
    <p className="sr-text">{i18next.t('common:quickActions')}</p>
    <ul>
      {friendPageHeaderLinks.top.map((link) => (
        <li className="nav__item">
          <svg className="nav__icon" width="30px" height="30px">
            <use xlinkHref={link.icon} />
          </svg>
          <Link url={link.url} className="nav__link">
            <span>{link.title}</span>
          </Link>
        </li>
      ))}
    </ul>
  </section>
)

type PageheaderContactItemsProps = { contactItems: ContactItem[] }

export const PageHeaderPhoneNumbersLayout = (
  props: PageheaderContactItemsProps & { phoneKind: 'default' | 'centrum' }
) => {
  const Phone = (number: ParsedPhoneNumber) => (
    <a className="nav__phone typo--l" href={number.uri} aria-labelledby={`a11y-${number.uri}`}>
      <span>{number.formatted}</span>
    </a>
  )
  const Description = (description: string) => {
    // Simple regex to check if the description is an email address. Not exhaustive but should work for most cases.
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(description)
    return isEmail ? (
      <a className="nav__email typo--m" href={`mailto:${description}`} id={`a11y-${description}`}>
        <span>{description}</span>
      </a>
    ) : (
      <p className="nav__hours typo--xs" id={`a11y-${description}`}>
        <span>{description}</span>
      </p>
    )
  }
  return props.contactItems && props.contactItems.length > 0 ? (
    <address className={`nav__contact nav__contact--${props.phoneKind}`}>
      <p className="sr-text">{i18next.t('common:phoneNumber_plural')}</p>
      <ul>
        {props.contactItems.map((item, index) => (
          <li key={`contact-item-${index}`}>
            {props.phoneKind == 'centrum' ? (
              <>
                {item.description && Description(item.description)}
                {item.telephone && Phone(item.telephone)}
              </>
            ) : (
              <>
                {item.telephone && Phone(item.telephone)}
                {item.description && Description(item.description)}
              </>
            )}
          </li>
        ))}
      </ul>
    </address>
  ) : (
    <></>
  )
}

type PageHeaderNavKinds =
  | ({ kind: 'on-page-nav'; toggleMenu?: () => void } & OnPageNavProps)
  | ({ kind: 'department'; toggleMenu?: () => void } & OnPageNavProps)
  | { kind: 'sub-department' }
  | { kind: 'centrum-department' }
  | ({ kind: 'centrum' } & OnPageNavProps)
  | ({ kind: 'friend-page'; toggleMenu?: () => void } & OnPageNavProps)

type PageHeaderNavProps = {
  centrumLogo?: { title: SpaarneCentrumType; url: string }
  navBottom?:
    | { kind: 'anchors'; items: { title: string; index: number }[] }
    | { kind: 'return'; title: SpaarneDepartmentType; url: string }
} & PageheaderQuicklinksProps &
  PageheaderContactItemsProps &
  PageHeaderNavKinds

const getDisplayClassName = (props: PageHeaderNavProps) => {
  // Hide the menu on mobile for all Baria Nederland subpages (campaign/landing pages).
  // The trailing slash excludes the Baria Nederland department page itself.
  const hiddenPathPrefixes = ['/afdelingen-en-centra/baria-nederland/']

  const path = window.location.pathname
  if (hiddenPathPrefixes.some((prefix) => path.startsWith(prefix))) {
    return 'mob-hide'
  } else {
    return ''
  }
}

export const PageHeaderNavLayout = (props: PageHeaderNavProps) => {
  return (
    <nav
      className={`${
        props.kind == 'department' || props.kind == 'sub-department' || props.kind == 'on-page-nav'
          ? 'pageheader__nav' // Generic 'green' styling
          : 'pageheader__top-tasks top-tasks' // Top tasks styling
      } ${props.kind != 'on-page-nav' ? 'nav--pageheader-department nav--pageheader-on-page-nav' : ''} ${
        props.kind != 'department' && props.kind != 'centrum-department' ? 'nav--pageheader-on-page-nav' : ''
      } ${getDisplayClassName(props)}`}
      aria-label={i18next.t('a11y:navLabelSecondary')}
    >
      {props.kind == 'on-page-nav' ? (
        // ----- ON-PAGE NAV (generic spaarne page)
        <>
          <ComponentDidMount onMount={() => window.addEventListener('scroll', onScrollOnPageNav)}>
            <div className="nav__wrap">
              {props.heading && <p className="nav__heading">{props.heading}</p>}
              <button
                className="nav__button"
                aria-controls="a11y-ph-nav"
                aria-expanded={props.menuOpened}
                onClick={props.toggleMenu}
              >
                <span className="button__label">{i18next.t('common:goQuicklyTo')}</span>
                <svg className="button__icon" aria-hidden="true" width="12px" height="12px">
                  <use xlinkHref="#icon--chevron-down" />
                </svg>
              </button>
              <OnPageNav menuOpened={props.menuOpened} items={props.items}/>
              <div className="nav__underlay" aria-hidden="true" onClick={props.toggleMenu} />
            </div>
          </ComponentDidMount>
          <ComponentWillUnmount onUnmount={() => window.removeEventListener('scroll', onScrollOnPageNav)} />
        </>
      ) : (
        // ----- OTHER META INFO (department/subdepartment/centrum/subcentrum)
        <>
          <div className="nav__top">
            {/* Centrum Logo */}
            {props.centrumLogo && (
              <Link
                url={props.centrumLogo.url}
                className="nav__logo"
                title={`Naar de homepage van het ${props.centrumLogo.title}`}
              >
                <span className="sr-text">{`Naar de homepage van het ${props.centrumLogo.title}`}</span>
                <PageHeaderNavLogo centrum={props.centrumLogo.title} className="nav__image" />
              </Link>
            )}
            {/* Friend page Logo */}
            {props.kind === 'friend-page' && (
              <Link url={'/vrienden'} className="nav__logo" title={`Naar de Vrienden pagina`}>
                <span className="sr-text">{`Naar de Vrienden pagina`}</span>
                <PageHeaderNavLogo className="nav__image" />
              </Link>
            )}
            {/* Phone numbers */}
            {props.contactItems && (
              <PageHeaderPhoneNumbersLayout phoneKind={props.kind == 'centrum' ? 'centrum' : 'default'} {...props} />
            )}
            {/* CTAs */}
            {(props.appointmentUrl || props.waitingTimesUrl) && (
              <PageHeaderQuicklinksLayout
                appointmentUrl={props.appointmentUrl}
                waitingTimesUrl={props.waitingTimesUrl}
                display={props.kind === 'department' ? 'display--desktop' : undefined}
              />
            )}
            {/* CTAs Friend Page */}
            {props.kind === 'friend-page' && <FriendPageHeaderQuicklinksLayout displayClassName="display--desktop" />}
            {/* Donation button for Friend pages */}
            {props.kind === 'friend-page' && (
              <div className="block__clickable">
                <Link url={`/vrienden/donatie`} className="button--primary">
                  <span className="button__label display--desktop">Doneer en word Vriend van Spaarne</span>
                  <span className="button__label display--mobile">Direct doneren</span>
                </Link>
              </div>
            )}
          </div>

          {/* Bottom */}
          {props.navBottom ||
          ((props.kind == 'centrum' || props.kind == 'friend-page') && props.items && props.items.length > 0) ? (
            <div className="nav__bottom">
              {props.navBottom && props.navBottom.kind == 'anchors' && (
                // Anchors (department & centra)
                <>
                  <p className="sr-text">{i18next.t('a11y:navigateParagraph')}</p>
                  <ul
                    className={`nav__anchors typo--xs ${props.kind === 'department' && 'display--desktop'}`}
                    id="a11y-ph-nav"
                    aria-hidden="true"
                  >
                    {props.navBottom.items.map((item) => (
                      <li className="nav__item" key={`anchor-${item.index}`}>
                        <a href={`#${item.index}`} className="nav__link">
                          <span>{item.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {props.kind == 'centrum' && props.items && props.items.length > 0 ? (
                // On-page nav/sibling-pages (centra/sub-centra)
                <>
                  <p className="sr-text">{i18next.t('a11y:navigateParagraph')}</p>
                  <OnPageNav menuOpened={true} items={props.items} />
                </>
              ) : props.kind === 'friend-page' ? (
                <>
                  <>
                    <ComponentDidMount onMount={() => window.addEventListener('scroll', onScrollOnPageNav)}>
                      <div className="nav__wrap">
                        <button
                          className="nav__button"
                          aria-controls="a11y-ph-nav"
                          aria-expanded={props.menuOpened}
                          onClick={props.toggleMenu}
                        >
                          <span className="button__label">{i18next.t('common:goQuicklyTo')}</span>
                          <svg className="button__icon" aria-hidden="true" width="12px" height="12px">
                            <use xlinkHref="#icon--chevron-down" />
                          </svg>
                        </button>
                        <OnPageNav
                          menuOpened={props.menuOpened}
                          items={props.items?.slice(0, 5)}
                          extraClasses="display--mobile"
                        />
                        <OnPageNav menuOpened={props.menuOpened} items={props.items?.slice(-2)} extraClasses={''} />
                        <div className="nav__underlay" aria-hidden="true" onClick={props.toggleMenu} />
                      </div>
                    </ComponentDidMount>
                    <ComponentWillUnmount onUnmount={() => window.removeEventListener('scroll', onScrollOnPageNav)} />
                  </>
                </>
              ) : props.kind === 'department' ? (
                <>
                  <ComponentDidMount onMount={() => window.addEventListener('scroll', onScrollOnPageNav)}>
                    <div className="nav__wrap display--mobile">
                      <button
                        className="nav__button"
                        aria-controls="a11y-ph-nav"
                        aria-expanded={props.menuOpened}
                        onClick={props.toggleMenu}
                      >
                        <span className="button__label">{i18next.t('common:goQuicklyTo')}</span>
                        <svg className="button__icon" aria-hidden="true" width="12px" height="12px">
                          <use xlinkHref="#icon--chevron-down" />
                        </svg>
                      </button>
                      {props.navBottom?.kind === 'anchors' && (
                        <OnPageNav
                          menuOpened={props.menuOpened}
                          items={
                            props.appointmentUrl && props.waitingTimesUrl
                              ? [
                                  {
                                    url: props.appointmentUrl,
                                    title: i18next.t('misc:makeAnAppointment'),
                                    active: window.location.href === props.appointmentUrl,
                                  },
                                  {
                                    url: props.waitingTimesUrl,
                                    title: i18next.t('misc:waitingTimes'),
                                    active: window.location.href === props.waitingTimesUrl,
                                  },
                                ]
                              : props.appointmentUrl
                              ? [
                                  {
                                    url: props.appointmentUrl,
                                    title: i18next.t('misc:makeAnAppointment'),
                                    active: window.location.href === props.appointmentUrl,
                                  },
                                ]
                              : props.waitingTimesUrl
                              ? [
                                  {
                                    url: props.waitingTimesUrl,
                                    title: i18next.t('misc:waitingTimes'),
                                    active: window.location.href === props.waitingTimesUrl,
                                  },
                                ]
                              : []
                          }
                          anchorItems={{ items: props.navBottom.items, toggleMenu: props.toggleMenu }}
                        />
                      )}
                      <div className="nav__underlay" aria-hidden="true" onClick={props.toggleMenu} />
                    </div>
                  </ComponentDidMount>
                  <ComponentWillUnmount onUnmount={() => window.removeEventListener('scroll', onScrollOnPageNav)} />
                </>
              ) : props.navBottom && props.navBottom.kind == 'return' ? (
                // Return (sub-department)
                <ul className="nav__return" id="a11y-ph-nav">
                  <li className="nav__item">
                    <div className="nav__visual--svg">
                      <DepartmentIcon
                        department={props.navBottom.title}
                        className="nav__icon"
                        width="50px"
                        height="50px"
                      />
                    </div>
                    <Link
                      url={props.navBottom.url}
                      className="nav__link"
                      title={`${i18next.t('common:returnTo')} ${props.navBottom.title}`}
                    >
                      <span>
                        <span className="sr-text">{i18next.t('common:returnTo')} </span>
                        {props.navBottom.title}
                      </span>
                    </Link>
                  </li>
                </ul>
              ) : (
                <></>
              )}
            </div>
          ) : (
            <></>
          )}
        </>
      )}
    </nav>
  )
}

type OnPageNavProps = {
  menuOpened: boolean
  heading?: string
  items?: { url: string; title: string; active: boolean }[]
  anchorItems?: { items: { title: string; index: number }[]; toggleMenu?: () => void }
  extraClasses?: string
}

const OnPageNav = (props: OnPageNavProps) => {
  return (
    <>
      <ul className={`nav__list--on-page-nav ${props.extraClasses}`} id="a11y-ph-nav" aria-hidden={!props.menuOpened}>
        {props.items &&
          props.items.map((item) => (
            <li className="nav__item" key={`on-page-nav-${item.title}`}>
              <Link url={item.url} className="nav__link" aria-current={item.active ? 'page' : undefined}>
                <span>{item.title}</span>
              </Link>
            </li>
          ))}
        {props.anchorItems &&
          props.anchorItems.items.map((item) => (
            <li className="nav__item" key={`anchor-${item.index}`}>
              <a href={`#${item.index}`} className="nav__link" onClick={props.anchorItems?.toggleMenu}>
                <span>{item.title}</span>
              </a>
            </li>
          ))}
      </ul>
    </>
  )
}

export const onScrollOnPageNav = () => {
  const onPageNav: HTMLElement | null = document.querySelector('.nav--pageheader-on-page-nav')
  const header: HTMLElement | null = document.querySelector('.header__toolbar')
  if (onPageNav == null || header == null) {
    return
  }
  if (window.innerWidth >= convertRemToPx(64)) {
    return
  }
  const headerThreshold: number = window.innerWidth >= convertRemToPx(48) ? 0 : header.getBoundingClientRect().height

  if (onPageNav.getBoundingClientRect().top <= headerThreshold) {
    onPageNav.classList.add('sticky')
  } else {
    onPageNav.classList.remove('sticky')
  }
}

type PageHeaderLink = {
  url: string
  title: string
  icon?: string
}

type FriendPageHeaderLinks = {
  top: PageHeaderLink[]
  bottom: PageHeaderLink[]
}

export const friendPageHeaderLinks: FriendPageHeaderLinks = {
  top: [
    { url: '/vrienden/kom-in-actie', title: 'Kom in actie', icon: '#icon--SG-actie' },
    { url: '/vrienden/shop', title: 'Vrienden shop', icon: '#icon--SG-shop' },
    { url: '/vrienden/word-bedrijfsvriend', title: 'Word bedrijfsvriend', icon: '#icon--SG-bedrijfsvriend' },
    { url: '/vrienden/nalatenschap', title: 'Nalatenschap', icon: '#icon--SG-nalatenschap' },
    { url: '/vrienden/projecten', title: 'Bekijk onze projecten', icon: '#icon--SG-projecten' },
  ],
  bottom: [
    { url: '/vrienden/over-ons', title: 'Over ons' },
    { url: '/vrienden/contactgegevens', title: 'Contact' },
  ],
}
